import { HubspotUser } from '../types/hubspot';

declare global {
  interface Window {
    _hsq: any[];
    HubSpotConversations: any;
    hsConversationsOnReady: (() => void)[];
    hsConversationsSettings: Record<string, any>;
  }
}

const HUBSPOT_PORTAL_ID = process.env.REACT_APP_HUBSPOT_PORTAL_ID || '';

/**
 * HubSpot Service
 * Dynamically loads HubSpot script and manages widget lifecycle.
 */
class HubspotService {
  private static instance: HubspotService;
  private isInitialized: boolean = false;
  private scriptLoaded: boolean = false;
  private currentUser: HubspotUser | null = null;

  private constructor() {}

  static getInstance(): HubspotService {
    if (!HubspotService.instance) {
      HubspotService.instance = new HubspotService();
    }
    return HubspotService.instance;
  }

  private get hsq(): any[] {
    window._hsq = window._hsq || [];
    return window._hsq;
  }

  /**
   * Inject the HubSpot tracking script into the page (once).
   */
  private loadScript(): void {
    if (this.scriptLoaded || !HUBSPOT_PORTAL_ID) return;
    this.scriptLoaded = true;

    // Tell HubSpot NOT to auto-show its default launcher
    window.hsConversationsSettings = {
      loadImmediately: false,
      enableWidgetCookieBanner: false,
    };

    // Register onReady BEFORE the script loads (HubSpot recommended pattern)
    const onReady = () => {
      console.log('HubSpot script loaded and ready');

      window.HubSpotConversations.widget.load();
    };

    if (window.HubSpotConversations) {
      onReady();
    } else {
      window.hsConversationsOnReady = [onReady];
    }

    const script = document.createElement('script');
    script.src = `//js-na1.hs-scripts.com/${HUBSPOT_PORTAL_ID}.js`;
    script.async = true;
    script.id = 'hs-script-loader';
    document.body.appendChild(script);
  }

  /**
   * Run a callback when HubSpotConversations is available, or immediately if already ready.
   */
  private whenReady(callback: () => void): void {
    if (window.HubSpotConversations) {
      callback();
    } else {
      window.hsConversationsOnReady = [
        ...(window.hsConversationsOnReady || []),
        callback,
      ];
    }
  }

  boot(user: HubspotUser): void {
    try {
      if (this.isInitialized) {
        this.update(user);
        return;
      }

      // Inject the script on first boot
      this.loadScript();

      this.hsq.push([
        'identify',
        {
          email: user.email,
          id: user.user_id,
        },
      ]);

      this.isInitialized = true;
      this.currentUser = user;
      console.log('HubSpot initialized successfully');
    } catch (error) {
      console.error('Failed to initialize HubSpot:', error);
    }
  }

  update(user: HubspotUser): void {
    try {
      if (!this.isInitialized) return;

      this.hsq.push([
        'identify',
        {
          email: user.email,
          id: user.user_id,
        },
      ]);

      this.currentUser = user;
    } catch (error) {
      console.error('Failed to update HubSpot:', error);
    }
  }

  show(): void {
    try {
      this.whenReady(() => {
        window.HubSpotConversations.widget.load();
        window.HubSpotConversations.widget.refresh();
        window.HubSpotConversations.widget.open();
      });
    } catch (error) {
      console.error('Failed to show HubSpot widget:', error);
    }
  }

  hide(): void {
    try {
      if (window.HubSpotConversations) {
        window.HubSpotConversations.widget.close();
      }
    } catch (error) {
      console.error('Failed to hide HubSpot widget:', error);
    }
  }

  shutdown(): void {
    try {
      if (window.HubSpotConversations) {
        window.HubSpotConversations.clear({ resetWidget: true });
      }
      this.isInitialized = false;
      this.currentUser = null;
      console.log('HubSpot shutdown successfully');
    } catch (error) {
      console.error('Failed to shutdown HubSpot:', error);
    }
  }

  isReady(): boolean {
    return this.isInitialized;
  }

  getCurrentUser(): HubspotUser | null {
    return this.currentUser;
  }
}

export default HubspotService.getInstance();
